import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { HotelLocationDropdown } from "../hotelSearchForm/HotelLocationDropdown";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import BannerCounter from "../../../../core/common/banner-counter/counter";

const HotelSearchForm: React.FC = () => {
  const navigate = useNavigate();
  const defaultDate = dayjs();

  const MAX_ROOMS = 6;
  const MAX_ADULTS_PER_ROOM = 3;
  const MAX_CHILDREN_PER_ROOM = 4;

  const DEFAULT_CHILD_AGE = 5;

  const [hotelSearchDetails, setHotelSearchDetails] = useState({
    city: {
      cityName: "New York",
      countryName: "USA",
      cityCode: "NYC",
      countryCode: "US",
    },
    checkInDate: defaultDate,
    checkOutDate: defaultDate.add(1, "day"),
    rooms: 1, // default min 1
    adults: 2,
    children: 0,
    // childrenAges is an array of arrays: childrenAges[roomIndex] = [age1, age2, ...]
    childrenAges: [[]] as number[][],
  });

  const [formError, setFormError] = useState<string | null>(null);

  // clamp helper
  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, Number(v) || min));

  // distribute children count evenly across rooms -> returns array length rooms with counts
  const distributeChildren = (rooms: number, totalChildren: number) => {
    const base = Math.floor(totalChildren / rooms);
    let rem = totalChildren - base * rooms;
    const arr = new Array(rooms).fill(0).map(() => {
      const c = base + (rem > 0 ? 1 : 0);
      if (rem > 0) rem--;
      return c;
    });
    return arr;
  };

  // ensure childrenAges structure matches rooms and counts (preserve existing ages if possible)
  // but now we default missing ages to DEFAULT_CHILD_AGE and never show inputs
  const normalizeChildrenAges = (
    prevAges: number[][],
    rooms: number,
    perRoomCounts: number[]
  ) => {
    const out: number[][] = [];
    for (let i = 0; i < rooms; i++) {
      const needed = perRoomCounts[i] || 0;
      const existing = (prevAges && prevAges[i]) ? prevAges[i].slice(0, needed) : [];
      while (existing.length < needed) {
        existing.push(DEFAULT_CHILD_AGE);
      }
      out.push(existing);
    }
    return out;
  };

  const handleInputChange = (field: string, value: any) => {
    // Rooms change: clamp to 1..MAX_ROOMS and adjust adults/children if needed
    if (field === "rooms") {
      const clampedRooms = clamp(value, 1, MAX_ROOMS);
      setHotelSearchDetails((prev) => {
        const maxAdultsAllowed = clampedRooms * MAX_ADULTS_PER_ROOM;
        const maxChildrenAllowed = clampedRooms * MAX_CHILDREN_PER_ROOM;

        const adjustedAdults = Math.min(prev.adults, maxAdultsAllowed);
        const adjustedChildren = Math.min(prev.children, maxChildrenAllowed);

        // redistribute children counts
        const perRoomCounts = distributeChildren(clampedRooms, adjustedChildren);
        const normalizedAges = normalizeChildrenAges(prev.childrenAges, clampedRooms, perRoomCounts);

        return {
          ...prev,
          rooms: clampedRooms,
          adults: adjustedAdults,
          children: adjustedChildren,
          childrenAges: normalizedAges,
        };
      });
      setFormError(null);
      return;
    }

    // Adults change: clamp to 1..rooms*MAX_ADULTS_PER_ROOM
    if (field === "adults") {
      setHotelSearchDetails((prev) => {
        const maxAdultsAllowed = (Number(prev.rooms) || 1) * MAX_ADULTS_PER_ROOM;
        const clampedAdults = clamp(value, 1, maxAdultsAllowed);
        return {
          ...prev,
          adults: clampedAdults,
        };
      });
      setFormError(null);
      return;
    }

    // Children change: clamp to 0..rooms*MAX_CHILDREN_PER_ROOM and regenerate childrenAges shape (defaults to 5)
    if (field === "children") {
      setHotelSearchDetails((prev) => {
        const maxChildrenAllowed = (Number(prev.rooms) || 1) * MAX_CHILDREN_PER_ROOM;
        const clampedChildren = clamp(value, 0, maxChildrenAllowed);

        const perRoomCounts = distributeChildren(Number(prev.rooms) || 1, clampedChildren);
        const normalizedAges = normalizeChildrenAges(prev.childrenAges, Number(prev.rooms) || 1, perRoomCounts);

        return {
          ...prev,
          children: clampedChildren,
          childrenAges: normalizedAges,
        };
      });
      setFormError(null);
      return;
    }

    // Updating dates or city
    setHotelSearchDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormError(null);
  };

  // helper to add one child (uses same clamp)
  const addOneChild = () => {
    setHotelSearchDetails((prev) => {
      const maxChildrenAllowed = (Number(prev.rooms) || 1) * MAX_CHILDREN_PER_ROOM;
      const newChildren = clamp(Number(prev.children || 0) + 1, 0, maxChildrenAllowed);
      const perRoomCounts = distributeChildren(Number(prev.rooms) || 1, newChildren);
      const normalizedAges = normalizeChildrenAges(prev.childrenAges, Number(prev.rooms) || 1, perRoomCounts);
      return {
        ...prev,
        children: newChildren,
        childrenAges: normalizedAges,
      };
    });
  };

  // helper to remove one child
  const removeOneChild = () => {
    setHotelSearchDetails((prev) => {
      const newChildren = Math.max(0, Number(prev.children || 0) - 1);
      const perRoomCounts = distributeChildren(Number(prev.rooms) || 1, newChildren);
      const normalizedAges = normalizeChildrenAges(prev.childrenAges, Number(prev.rooms) || 1, perRoomCounts);
      return {
        ...prev,
        children: newChildren,
        childrenAges: normalizedAges,
      };
    });
  };

  /**
   * Build pax rooms safely and validate.
   * Returns { paxRooms, error }
   */
  const buildPaxRoomsSafely = (): { paxRooms?: any[]; error?: string } => {
    const rooms = Number(hotelSearchDetails.rooms) || 1;
    const totalAdults = Number(hotelSearchDetails.adults) || 0;
    const totalChildren = Number(hotelSearchDetails.children) || 0;

    if (rooms <= 0) return { error: "Rooms must be at least 1." };

    // enforce max rooms defensively
    if (rooms > MAX_ROOMS) {
      return { error: `Maximum ${MAX_ROOMS} rooms allowed per search.` };
    }

    if (totalAdults > rooms * MAX_ADULTS_PER_ROOM) {
      return { error: `Maximum ${MAX_ADULTS_PER_ROOM} adults allowed per room.` };
    }
    if (totalChildren > rooms * MAX_CHILDREN_PER_ROOM) {
      return { error: `Maximum ${MAX_CHILDREN_PER_ROOM} children allowed per room.` };
    }

    // derive per-room counts the same way as the UI
    const perRoomCounts = distributeChildren(rooms, totalChildren);

    // childrenAges are filled with DEFAULT_CHILD_AGE by normalizeChildrenAges, so no age inputs required.
    const ages = normalizeChildrenAges(hotelSearchDetails.childrenAges, rooms, perRoomCounts);

    // Initialize paxRooms
    const paxRooms: any[] = Array.from({ length: rooms }, () => ({
      Adults: 0,
      Children: 0,
      ChildrenAges: [] as number[],
    }));

    // Evenly distribute adults
    const baseAdults = Math.floor(totalAdults / rooms);
    let remAdults = totalAdults - baseAdults * rooms;
    for (let i = 0; i < rooms; i++) {
      paxRooms[i].Adults = baseAdults + (remAdults > 0 ? 1 : 0);
      if (remAdults > 0) remAdults--;
    }

    // Assign children counts & ages based on perRoomCounts
    for (let i = 0; i < rooms; i++) {
      const childrenCount = perRoomCounts[i] || 0;
      paxRooms[i].Children = childrenCount;
      paxRooms[i].ChildrenAges = ages[i] ? ages[i].slice(0, childrenCount) : Array.from({ length: childrenCount }, () => DEFAULT_CHILD_AGE);
    }

    // Final validation per room (adults/children per room)
    for (let i = 0; i < rooms; i++) {
      if (paxRooms[i].Adults > MAX_ADULTS_PER_ROOM) {
        return { error: `Room ${i + 1} exceeds max ${MAX_ADULTS_PER_ROOM} adults.` };
      }
      if (paxRooms[i].Children > MAX_CHILDREN_PER_ROOM) {
        return { error: `Room ${i + 1} exceeds max ${MAX_CHILDREN_PER_ROOM} children.` };
      }
      if (paxRooms[i].ChildrenAges.length !== paxRooms[i].Children) {
        return { error: `ChildrenAges count mismatch for room ${i + 1}.` };
      }
    }

    return { paxRooms };
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // build pax rooms
    const { paxRooms, error } = buildPaxRoomsSafely();
    if (error) {
      setFormError(error);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // prepare state payload (dates as YYYY-MM-DD)
    const payload = {
      ...hotelSearchDetails,
      checkInDate: hotelSearchDetails.checkInDate.format("YYYY-MM-DD"),
      checkOutDate: hotelSearchDetails.checkOutDate.format("YYYY-MM-DD"),
      PaxRooms: paxRooms,
      GuestNationality: "IN",
    };

    navigate(all_routes.hotelGrid, {
      state: { hotelSearchDetails: payload },
    });
  };

  // derive per-room children counts for rendering
  const renderPerRoomCounts = distributeChildren(
    Number(hotelSearchDetails.rooms) || 1,
    Number(hotelSearchDetails.children) || 0
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
        <h6 className="fw-medium fs-16 mb-2">
          Book Hotel - Villas, Apartments & more.
        </h6>
      </div>

      {formError && (
        <div className="alert alert-danger">
          {formError}
        </div>
      )}

      <div className="d-lg-flex">
        <div className="d-flex form-info">
          {/* City selector */}
          <HotelLocationDropdown
            label="City"
            defaultCity={hotelSearchDetails.city.cityName}
            defaultCountry={hotelSearchDetails.city.countryName}
            onChange={(value) => handleInputChange("city", value)}
          />

          {/* Check In */}
          <div className="form-item">
            <label className="form-label fs-14 text-default mb-1">Check In</label>
            <DatePicker
              className="form-control datetimepicker"
              placeholder="dd/mm/yyyy"
              value={hotelSearchDetails.checkInDate}
              format="DD-MM-YYYY"
              onChange={(date: Dayjs | null) =>
                handleInputChange("checkInDate", date || defaultDate)
              }
            />
            <p className="fs-12 mb-0">
              {hotelSearchDetails.checkInDate.format("dddd")}
            </p>
          </div>

          {/* Check Out */}
          <div className="form-item">
            <label className="form-label fs-14 text-default mb-1">Check Out</label>
            <DatePicker
              className="form-control datetimepicker"
              placeholder="dd/mm/yyyy"
              value={hotelSearchDetails.checkOutDate}
              format="DD-MM-YYYY"
              onChange={(date: Dayjs | null) =>
                handleInputChange(
                  "checkOutDate",
                  date || defaultDate.add(1, "day")
                )
              }
            />
            <p className="fs-12 mb-0">
              {hotelSearchDetails.checkOutDate.format("dddd")}
            </p>
          </div>

          {/* Guests & Rooms */}
          <div className="form-item dropdown">
            <div data-bs-toggle="dropdown" data-bs-auto-close="outside" role="menu">
              <label className="form-label fs-14 text-default mb-1">
                Guests & Rooms
              </label>
              <h5>
                {hotelSearchDetails.adults + hotelSearchDetails.children}{" "}
                <span className="fw-normal fs-14">Persons</span>
              </h5>
              <p className="fs-12 mb-0">
                {hotelSearchDetails.adults} Adult, {hotelSearchDetails.children} Children,{" "}
                {hotelSearchDetails.rooms} Rooms
              </p>
            </div>

            <div className="dropdown-menu dropdown-menu-end dropdown-xl">
              <h5 className="mb-3">Select Guests & Rooms</h5>
              <div className="mb-3 border br-10 info-item pb-1">
                <div className="row align-items-end">
                  <div className="col-md-4">
                    <label className="form-label text-gray-9 mb-2">Rooms</label>
                    <BannerCounter
                      value={hotelSearchDetails.rooms}
                      onChange={(value) => handleInputChange("rooms", value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-gray-9 mb-2">Adults</label>
                    <BannerCounter
                      value={hotelSearchDetails.adults}
                      onChange={(value) => handleInputChange("adults", value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-gray-9 mb-2">Children</label>

                    <div className="d-flex gap-2 align-items-center">
                      {/* keep BannerCounter for accessibility */}
                      <BannerCounter
                        value={hotelSearchDetails.children}
                        onChange={(value) => handleInputChange("children", value)}
                      />

                      {/* explicit add/remove buttons so user always has a visible way to add a child */}
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={addOneChild}
                        >
                          + Child
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={removeOneChild}
                          disabled={hotelSearchDetails.children <= 0}
                        >
                          - Child
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3">
                <small className="text-muted">
                  Note: Child ages are set to <strong>{DEFAULT_CHILD_AGE}</strong> by default. You can modify this later if needed.
                </small>
                <small className="text-muted d-block mt-1">
                  <strong>Note:</strong> Guest nationality is set to <strong>India</strong> by default.{" "}
                  Other nationalities are currently not supported for booking.
                </small>
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary search-btn rounded">
          Search
        </button>
      </div>
    </form>
  );
};

export default HotelSearchForm;
